import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import LoadingPage from '@/components/common/LoadingPage';
import SubPageHeader from '@/components/common/SubPageHeader';
import { ToggleGroup, ToggleGroupItem } from '@/components/shadcn/toggle-group';
import hasItems from '@/lib/utils/hasItems';

import useSelectedParty from '../analysis/hooks/useSelectedParty';

import ClaimItem from './components/ClaimsSection/ClaimItem';
import useClaims from './queries/hooks/useClaims';
import type { ClaimStatus } from './types';

const getStatusFilter = (status: Maybe<string>): ClaimStatus[] => {
  if (status === 'ALL') return [];
  if (status === 'CLOSED') return ['CLOSED'];

  return ['OPEN'];
};

export default function Detail() {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const handleValueChange = (value?: string) =>
    setSearchParams(
      {
        status: (value ?? 'OPEN').toString(),
      },
      { replace: true },
    );

  const party = useSelectedParty();

  const statusFilter = getStatusFilter(searchParams.get('status'));

  const { data: claims, isLoading } = useClaims(
    party?.id ?? '',
    { status: statusFilter },
    {
      enabled: Boolean(party?.id),
    },
  );

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <SubPageHeader>{t('CLAIMS_TITLE')}</SubPageHeader>

      <main className="flex flex-1 flex-col gap-2 overflow-y-auto pb-4">
        <div className="mt-2 flex flex-col gap-1 p-2">
          <ToggleGroup
            type="single"
            value={searchParams.get('status') ?? 'OPEN'}
            onValueChange={handleValueChange}
            className="border"
          >
            <ToggleGroupItem value="OPEN" className="cursor-pointer">
              {t('CLAIMS_FILTER_STATUS_OPEN')}
            </ToggleGroupItem>

            <ToggleGroupItem value="CLOSED" className="cursor-pointer">
              {t('CLAIMS_FILTER_STATUS_CLOSED')}
            </ToggleGroupItem>

            <ToggleGroupItem value="ALL" className="cursor-pointer">
              {t('CLAIMS_FILTER_STATUS_ALL')}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {isLoading && <LoadingPage />}

        {!isLoading && hasItems(claims) && (
          <div className="flex flex-col gap-2 px-2">
            {claims?.map((claim) => (
              <ClaimItem key={claim.id} claim={claim} />
            ))}
          </div>
        )}

        {!isLoading && !hasItems(claims) && (
          <div className="text-muted-foreground flex w-full items-center justify-center">
            {t('CLAIMS_OVERVIEW_EMPTY')}
          </div>
        )}
      </main>
    </div>
  );
}
